/**
 * TanStack Query hook for debounced slide search.
 */

import { useEffect, useState } from "react"

import { useQuery } from "@tanstack/react-query"

import type { SlideSenseSlide } from "../types"

import { searchSlides } from "../api"

// ---------------------------------------------------------------------------
// useDebounce helper
// ---------------------------------------------------------------------------

/**
 * Debounce a value by the given delay in milliseconds.
 * Returns the latest value only after the delay has elapsed without changes.
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])

  return debouncedValue
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseSlideSearchOptions {
  /** Legacy single-folder scope; ignored when `folderIds` is non-empty. */
  folderId?: string
  category?: string
  /** Multi-folder scope chips. */
  folderIds?: string[]
  /** Saved-presentation scope chips — filter results to slides that appear
   *  in at least one of these collections. */
  collectionIds?: string[]
}

/**
 * Search slides with a debounced query string.
 * The query is only sent after the user stops typing for 300ms and
 * the search term has at least 2 characters.
 */
export function useSlideSearch(query: string, options: UseSlideSearchOptions = {}) {
  const { folderId, category, folderIds, collectionIds } = options
  const debouncedQuery = useDebounce(query, 300)

  // Stable string keys for the queryKey + the comma-separated payload sent
  // to the backend's `folder_ids`/`collection_ids` query params.
  const folderIdsKey = folderIds && folderIds.length > 0 ? folderIds.join(",") : undefined
  const collectionIdsKey =
    collectionIds && collectionIds.length > 0 ? collectionIds.join(",") : undefined

  return useQuery<SlideSenseSlide[]>({
    queryKey: [
      "slide-sense",
      "search",
      debouncedQuery,
      folderId,
      category,
      folderIdsKey,
      collectionIdsKey,
    ],
    queryFn: () =>
      searchSlides({
        q: debouncedQuery,
        folder_id: folderId,
        category,
        folder_ids: folderIdsKey,
        collection_ids: collectionIdsKey,
      }),
    enabled: debouncedQuery.length >= 2,
  })
}
